// Email-first auth. Existing accounts sign in with email + password.
// New accounts confirm an email OTP, then set a password and first-time phone.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Lock, Mail, Phone, ShieldCheck } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("email"); // email | password | otp | setup | forgot
  const [loading, setLoading] = useState(false);
  const [devCode, setDevCode] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();

  const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value);

  const startEmailLookup = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/password/request", { email });
      if (data.exists) {
        setStep("password");
        toast.info("Account found. Enter your password to continue.");
      } else {
        setStep("otp");
        setOtp(data.dev_code || "");
        setDevCode(data.dev_code || "");
        if (data.dev_code) {
          toast.success(`OTP_BYPASS: dev code is ${data.dev_code}`);
        } else {
          toast.success("Check your email inbox for the 6-digit code.");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to continue");
    } finally {
      setLoading(false);
    }
  };

  const loginWithPassword = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter your password");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/password/login", { email, password });
      login(data.token, data.user);
      toast.success("Signed in");
      nav(data.user.name ? "/me" : "/me");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 4) {
      toast.error("Enter the 6-digit OTP code");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/password/otp/verify", { email, otp });
      setStep("setup");
      toast.success("Email verified. Set your password and phone number.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const completeSetup = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (phone.replace(/\D/g, "").length < 6) {
      toast.error("Enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/password/setup", { email, password, phone, otp });
      login(data.token, data.user);
      toast.success("Account created and signed in");
      nav(data.user.name ? "/me" : "/me");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not finish account setup");
    } finally {
      setLoading(false);
    }
  };

  const requestForgotOtp = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/password/otp/request", { email });
      setStep("forgot");
      setOtp(data.dev_code || "");
      setDevCode(data.dev_code || "");
      if (data.dev_code) {
        toast.success(`OTP_BYPASS: dev code is ${data.dev_code}`);
      } else {
        toast.success("A reset code was sent to your email.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send reset OTP");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/password/reset", { email, otp, password });
      login(data.token, data.user);
      toast.success("Password reset successfully");
      nav(data.user.name ? "/me" : "/me");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  const backToEmail = () => {
    setStep("email");
    setPassword("");
    setOtp("");
    setPhone("");
    setDevCode("");
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-15"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/12075685/pexels-photo-12075685.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/95 to-obsidian" />

      <div className="relative w-full max-w-md border border-zinc-800 bg-charcoal p-8">
        <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court mb-4">Sign In · Sign Up</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter leading-none">Your email is your access key.</h1>
        <p className="text-sm text-zinc-400 mt-3">Use your email, set a password, and complete your first-time phone number.</p>

        {step === "email" && (
          <form onSubmit={startEmailLookup} className="mt-8 space-y-4">
            <label className="block">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Email Address</span>
              <div className="mt-2 flex items-center bg-transparent border border-zinc-800 focus-within:border-court">
                <Mail className="w-4 h-4 mx-3 text-zinc-500" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="player@example.com"
                  className="flex-1 bg-transparent py-3 pr-3 outline-none text-white"
                  autoFocus
                />
              </div>
            </label>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-court text-white font-display font-black tracking-wider uppercase border-b-4 border-court-deep hover:bg-court-hover active:translate-y-0.5 active:border-b-0 transition-all disabled:opacity-50"
            >
              {loading ? "Checking…" : "Continue"}
            </button>
            <div className="text-[10px] tracking-widest uppercase text-zinc-500 text-center">
              New users receive a 6-digit OTP at their email address.
            </div>
          </form>
        )}

        {step === "password" && (
          <form onSubmit={loginWithPassword} className="mt-8 space-y-4">
            <label className="block">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Password</span>
              <div className="mt-2 flex items-center bg-transparent border border-zinc-800 focus-within:border-court">
                <Lock className="w-4 h-4 mx-3 text-zinc-500" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="flex-1 bg-transparent py-3 pr-3 outline-none text-white"
                  autoFocus
                />
              </div>
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-court text-white font-display font-black tracking-wider uppercase border-b-4 border-court-deep hover:bg-court-hover active:translate-y-0.5 active:border-b-0 transition-all disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
            <button
              type="button"
              onClick={requestForgotOtp}
              className="w-full h-10 text-xs font-bold tracking-widest uppercase text-zinc-400 hover:text-white"
            >
              Forgot password?
            </button>
            <button
              type="button"
              onClick={backToEmail}
              className="w-full h-10 text-xs font-bold tracking-widest uppercase text-zinc-400 hover:text-white"
            >
              ← Back to email
            </button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={verifyEmailOtp} className="mt-8 space-y-4">
            <label className="block">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Email Verification Code</span>
              <div className="mt-2 flex items-center bg-transparent border border-zinc-800 focus-within:border-court">
                <ShieldCheck className="w-4 h-4 mx-3 text-zinc-500" />
                <input
                  data-testid="login-otp-input"
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={8}
                  placeholder="123456"
                  className="flex-1 bg-transparent py-3 pr-3 outline-none text-white font-mono tracking-[0.4em] text-lg"
                  autoFocus
                />
              </div>
              {devCode && (
                <div className="mt-2 text-[10px] tracking-widest uppercase text-zinc-500">
                  Dev code prefilled: <span className="font-mono text-court">{devCode}</span>
                </div>
              )}
            </label>
            <button
              data-testid="login-verify-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-court text-white font-display font-black tracking-wider uppercase border-b-4 border-court-deep hover:bg-court-hover active:translate-y-0.5 active:border-b-0 transition-all disabled:opacity-50"
            >
              {loading ? "Verifying…" : "Verify & Continue"}
            </button>
            <button
              type="button"
              onClick={backToEmail}
              className="w-full h-10 text-xs font-bold tracking-widest uppercase text-zinc-400 hover:text-white"
            >
              ← Back to email
            </button>
          </form>
        )}

        {step === "setup" && (
          <form onSubmit={completeSetup} className="mt-8 space-y-4">
            <label className="block">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Create Password</span>
              <div className="mt-2 flex items-center bg-transparent border border-zinc-800 focus-within:border-court">
                <Lock className="w-4 h-4 mx-3 text-zinc-500" />
                <input
                  data-testid="register-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="flex-1 bg-transparent py-3 pr-3 outline-none text-white"
                  autoFocus
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Phone Number</span>
              <div className="mt-2 flex items-center bg-transparent border border-zinc-800 focus-within:border-court">
                <Phone className="w-4 h-4 mx-3 text-zinc-500" />
                <input
                  data-testid="register-phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="9999999999"
                  className="flex-1 bg-transparent py-3 pr-3 outline-none text-white font-mono"
                />
              </div>
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-court text-white font-display font-black tracking-wider uppercase border-b-4 border-court-deep hover:bg-court-hover active:translate-y-0.5 active:border-b-0 transition-all disabled:opacity-50"
            >
              {loading ? "Saving…" : "Finish Setup"}
            </button>
          </form>
        )}

        {step === "forgot" && (
          <form onSubmit={resetPassword} className="mt-8 space-y-4">
            <label className="block">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Reset OTP</span>
              <div className="mt-2 flex items-center bg-transparent border border-zinc-800 focus-within:border-court">
                <ShieldCheck className="w-4 h-4 mx-3 text-zinc-500" />
                <input
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={8}
                  placeholder="Enter the 6-digit code"
                  className="flex-1 bg-transparent py-3 pr-3 outline-none text-white font-mono tracking-[0.4em] text-lg"
                  autoFocus
                />
              </div>
              {devCode && (
                <div className="mt-2 text-[10px] tracking-widest uppercase text-zinc-500">
                  Dev code prefilled: <span className="font-mono text-court">{devCode}</span>
                </div>
              )}
            </label>
            <label className="block">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">New Password</span>
              <div className="mt-2 flex items-center bg-transparent border border-zinc-800 focus-within:border-court">
                <Lock className="w-4 h-4 mx-3 text-zinc-500" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Set a new password"
                  className="flex-1 bg-transparent py-3 pr-3 outline-none text-white"
                />
              </div>
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-court text-white font-display font-black tracking-wider uppercase border-b-4 border-court-deep hover:bg-court-hover active:translate-y-0.5 active:border-b-0 transition-all disabled:opacity-50"
            >
              {loading ? "Resetting…" : "Reset Password"}
            </button>
            <button
              type="button"
              onClick={backToEmail}
              className="w-full h-10 text-xs font-bold tracking-widest uppercase text-zinc-400 hover:text-white"
            >
              ← Back to email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
